import {
  CheckCircleFilled,
  ClockCircleOutlined,
  EditOutlined,
  EnvironmentOutlined,
  FileOutlined,
  InfoCircleOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Space,
  Alert,
  Divider,
  Col,
  Row,
  Typography,
  Avatar,
  Button,
  Modal,
  theme,
  message,
  Drawer,
  Tabs,
  TabsProps,
  Radio,
  ConfigProvider,
  Collapse,
  Empty,
  Tag,
  Card,
} from "antd";
import { useContext, useEffect, useState } from "react";
import { AuthenticationContext } from "../../../contexts/authentication-context";
import { CompanyContext } from "../../../contexts/company-context";
import Unauthorized from "../../../errors/internal-server";
import { EmployeeOperation } from "../../../serviceoperation/EmployeeOperation";
import { ScheduleDetails } from "../../../serviceoperation/entity/ScheduleOperationEntity";
import { ScheduleOperation } from "../../../serviceoperation/ScheduleOperation";
import { AmPMFormat, ConvertTo12hr } from "../../../Util/dateutil";
import TimeOffRequestCancel from "../../../components/schedule/modal/timeoff-request-cancel";
import TimeOffModal from "../../../components/schedule/modal/timeoff-schedule-request";
import ScheduleQuickAction from "../../../components/schedule/schedule-quickaction";
import { timeOffStatus } from "../../../constants";
import FindScheduleReplacement from "../../../components/schedule/modal/findScheduleReplacement";
import EditScheduleForm from "../../../components/schedule/schedule-detail-edit";
import {
  getAlertType,
  syncStatusConfig,
} from "../../../components/employees/helpers/syncStatusConfig";
import ScheduleActivity from "../../../components/schedule/schedule-activity";
import ScheduleTaskManagerModal from "../../../components/schedule/modal/schedule-task-manager-modal";
import AbsentScheduleModal from "../../../components/schedule/modal/AbsentScheduleModal";

interface ScheduleDetailsDrawerProps {
  scheduleId: number;
  open: boolean;
  onClose: () => void;
  onRefresh?: () => void;
}

const ScheduleDetailsDrawer: React.FC<ScheduleDetailsDrawerProps> = ({
  scheduleId,
  open,
  onClose,
  onRefresh,
}) => {
  const { selectedCompany } = useContext(CompanyContext);
  const { Text, Title } = Typography;
  const { useToken } = theme;
  const { token } = useToken();
  const [openTieOffModal, setOpenTieOffModal] = useState(false);
  const [openTimOffCancelModal, setOpenTimOffCancelModal] = useState(false);
  const [updateSchedule, SetupdateSchedule] = useState(false);
  const userInfo = useContext(AuthenticationContext)?.userInfo;
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState<string>("detail");
  const [openTaskManagerModal, setOpenTaskManagerModal] = useState(false);
  const [completingTaskId, setCompletingTaskId] = useState<string>();
  const [openAbsentModal, setOpenAbsentModal] = useState(false);

  const onCreateTimeOff = () => {
    setOpenTieOffModal(false);
    SetupdateSchedule(!updateSchedule);
    message.success("Successfully processed your request.");
  };

  const handleEditClick = () => {
    setIsFormOpen(true);
  };

  const handleCancel = () => {
    setIsFormOpen(false);
  };

  const onCancelTimeOff = () => {
    setOpenTimOffCancelModal(false);
    SetupdateSchedule(!updateSchedule);
    message.success("Successfully processed your request.");
  };

  const onCreateReplacement = () => {
    setOpenFindReplacementModel(false);
    SetupdateSchedule(!updateSchedule);
    message.success("Successfully processed your request.");
  };

  const getApproxLocationFromIp = async (): Promise<{
    latitude?: number;
    longitude?: number;
    provider?: string;
  }> => {
    try {
      const response = await fetch("https://ipapi.co/json/");
      if (response.ok) {
        const data = (await response.json()) as {
          latitude?: number;
          longitude?: number;
        };
        if (
          typeof data.latitude === "number" &&
          typeof data.longitude === "number"
        ) {
          return {
            latitude: data.latitude,
            longitude: data.longitude,
            provider: "ipapi",
          };
        }
      }
    } catch {
      // Ignore and try fallback provider
    }

    try {
      const response = await fetch("https://ipwho.is/");
      if (response.ok) {
        const data = (await response.json()) as {
          success?: boolean;
          latitude?: number;
          longitude?: number;
        };
        if (
          data.success !== false &&
          typeof data.latitude === "number" &&
          typeof data.longitude === "number"
        ) {
          return {
            latitude: data.latitude,
            longitude: data.longitude,
            provider: "ipwhois",
          };
        }
      }
    } catch {
      // Ignore and continue without coordinates
    }

    return {};
  };

  const handleCompleteTask = async (taskId: string) => {
    if (!selectedCompany) {
      return;
    }

    try {
      setCompletingTaskId(taskId);
      const approxLocation = await getApproxLocationFromIp();
      const scheduleOps = new ScheduleOperation();
      const result = await scheduleOps.completeScheduleTaskAsync(
        selectedCompany.corporationId,
        taskId,
        {
          latitude: approxLocation.latitude,
          longitude: approxLocation.longitude,
          deviceInfo: `manager-drawer${
            approxLocation.provider ? `-ip:${approxLocation.provider}` : ""
          }`,
        },
      );

      if (result.statusCode === 200) {
        message.success("Task marked as complete.");
        SetupdateSchedule(!updateSchedule);
        onRefresh?.();
      } else {
        message.error(
          result.statusMessage ||
            "Unable to complete task. Please try again later.",
        );
      }
    } catch {
      message.error("Unable to complete task. Please try again later.");
    } finally {
      setCompletingTaskId(undefined);
    }
  };

  const [confirmLoading, setConfirmLoading] = useState(false);
  const [cancelScheduleOpen, setCancelScheduleOpen] = useState(false);
  const [openFindReplacementModel, setOpenFindReplacementModel] =
    useState(false);

  const handleCancelScheduleOk = async () => {
    setConfirmLoading(true);
    const scheduleOps = new ScheduleOperation();
    await scheduleOps
      .deleteScheduleAsync(selectedCompany?.corporationId as number, scheduleId)
      .then((x) => {
        if (x.status === 204) {
          setCancelScheduleOpen(false);
          message.success("Successfully processed your request!");
          onClose();
          onRefresh?.();
        } else if (x.status === 400) {
          message.error(
            "This schedule includes existing activities. Please make adjustments to the activities before proceeding with the deletion of this schedule.",
          );
        } else {
          message.error("There was an error trying to process your request!");
        }
      })
      .catch(() => {
        message.error("There was an error trying to process your request!");
      });
    setConfirmLoading(false);
  };

  const showCancleScheduleModal = () => {
    setCancelScheduleOpen(true);
  };

  const showFindReplacementModel = () => {
    setOpenFindReplacementModel(true);
  };

  const [scheduleDetail, setScheduleDetail] = useState<ScheduleDetails>();
  const [isManager, setIsManager] = useState<boolean>(false);

  const hasNoActivity = (scheduleDetail?.shiftActivity?.length ?? 0) === 0;
  const isOnOrBeforeToday = scheduleDetail
    ? new Date(scheduleDetail.workDate) <=
      new Date(new Date().setHours(0, 0, 0, 0))
    : false;

  const handleMarkAbsent = async (absentReason: string): Promise<boolean> => {
    if (!selectedCompany) {
      return false;
    }

    try {
      const scheduleOps = new ScheduleOperation();
      const result = await scheduleOps.patchScheduleAbsentAsync(
        selectedCompany.corporationId,
        scheduleId,
        absentReason,
      );

      if (result.statusCode === 200) {
        SetupdateSchedule(!updateSchedule);
        onRefresh?.();
        return true;
      }

      return false;
    } catch {
      return false;
    }
  };

  const getDayWithOrdinal = (day: number) => {
    if (day > 3 && day < 21) return `${day}th`;
    switch (day % 10) {
      case 1:
        return `${day}st`;
      case 2:
        return `${day}nd`;
      case 3:
        return `${day}rd`;
      default:
        return `${day}th`;
    }
  };

  const getDrawerTitle = () => {
    if (!scheduleDetail) {
      return "Schedule Details";
    }

    const workDate = new Date(scheduleDetail.workDate);
    const weekday = workDate.toLocaleDateString("en-us", {
      weekday: "long",
      timeZone: "UTC",
    });
    const month = workDate.toLocaleDateString("en-us", {
      month: "long",
      timeZone: "UTC",
    });
    const year = workDate.toLocaleDateString("en-us", {
      year: "numeric",
      timeZone: "UTC",
    });
    const day = getDayWithOrdinal(workDate.getUTCDate());
    const employeeName = `${scheduleDetail.employee.firstName} ${scheduleDetail.employee.lastName}`;

    return `${employeeName} - Schedule ${weekday}, ${month} ${day} ${year}`;
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  useEffect(() => {
    if (!open) return;

    const scheduleOps = new ScheduleOperation();
    scheduleOps
      .getScheduleByIdAsync(
        selectedCompany?.corporationId as number,
        scheduleId,
      )
      .then((x) => {
        setScheduleDetail(x);
      })
      .catch(() => {
        // Unable to load schedule
      });
  }, [updateSchedule, open]);

  useEffect(() => {
    const employeeOps = new EmployeeOperation();

    if (scheduleDetail && selectedCompany) {
      const loggedInUserID = userInfo?.employeeCorporation?.find(
        (y) => y.corporationId === selectedCompany.corporationId,
      )?.employeeId;

      if (scheduleDetail.employee.employeeID === loggedInUserID) {
        setIsManager(false);
      } else {
        employeeOps
          .getEmployeeByIdAsync(
            selectedCompany.corporationId,
            scheduleDetail.employee.employeeID,
          )
          .then((x) => {
            if (x) {
              setIsManager(true);
            } else {
              return <Unauthorized displayHome={true} />;
            }
          })
          .catch(() => <Unauthorized displayHome={true} />);
      }
    }
  }, [scheduleDetail, selectedCompany, userInfo]);

  const onCreate = (values: ScheduleDetails) => {
    console.log("Received values of form: ", values);
    setIsFormOpen(false);
    SetupdateSchedule(!updateSchedule);
    message.success("Successfully processed your request.");
    onRefresh?.();
  };

  const tabItems: TabsProps["items"] = [
    {
      key: "detail",
      label: "Details",
      children: (
        <div style={{ overflowY: "auto", maxHeight: "calc(100vh - 200px)" }}>
          {["Failed", "Pending"].includes(scheduleDetail?.syncStatus || "") && (
            <Alert
              message={
                scheduleDetail?.syncStatus &&
                syncStatusConfig("TimeEntry")[scheduleDetail.syncStatus]
                  ? syncStatusConfig("TimeEntry")[scheduleDetail.syncStatus]
                      .tooltip
                  : "There was an error processing your request. Please try again later."
              }
              type={getAlertType(scheduleDetail?.syncStatus)}
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {scheduleDetail?.timeoffDetail ? (
            <Space
              direction="vertical"
              style={{ width: "100%", marginBottom: "20px" }}
            >
              <Alert
                message={
                  scheduleDetail.timeoffDetail.timeOffStatusId ===
                  timeOffStatus.APPROVE
                    ? "Note: Your time-off request for this schedule has been Approved by your manager."
                    : scheduleDetail.timeoffDetail.timeOffStatusId ===
                      timeOffStatus.DENY
                    ? "Note: Your time-off request for this schedule has been rejected by your manager."
                    : scheduleDetail.timeoffDetail.timeOffStatusId ===
                      timeOffStatus.CANCEL
                    ? "Note: Your time-off request for this schedule has been canceled."
                    : "Note: Currently this request is pending time off approval. You may request to cancel your time off request using the option below."
                }
                type="info"
                showIcon
              />
            </Space>
          ) : null}

          {scheduleDetail?.replacementRequest ? (
            <Space
              direction="vertical"
              style={{ width: "100%", marginBottom: "20px" }}
            >
              <Alert
                message={
                  "Note: Currently this schedule is pending swap request approval."
                }
                type="info"
                showIcon
              />
            </Space>
          ) : null}

          {scheduleDetail?.isAbsent ? (
            <Space
              direction="vertical"
              style={{ width: "100%", marginBottom: "20px" }}
            >
              <Alert
                message={
                  <div>
                    <strong>This schedule is marked as absent.</strong>
                    {scheduleDetail.absentReason ? (
                      <div style={{ marginTop: 8 }}>
                        Reason: {scheduleDetail.absentReason}
                      </div>
                    ) : null}
                    {scheduleDetail.absenceMarkedDateTimeUTC ? (
                      <div style={{ marginTop: 4 }}>
                        Marked at:{" "}
                        {new Date(
                          scheduleDetail.absenceMarkedDateTimeUTC,
                        ).toLocaleString()}
                      </div>
                    ) : null}
                  </div>
                }
                type="error"
                showIcon
              />
            </Space>
          ) : null}

          {scheduleDetail && new Date(scheduleDetail.workDate) < today ? (
            <Space
              direction="vertical"
              style={{ width: "100%", marginBottom: "20px" }}
            >
              <Alert
                message="Note: This schedule has occurred in the past."
                type="warning"
                showIcon
              />
            </Space>
          ) : null}

          {scheduleDetail ? (
            <ScheduleQuickAction
              schedleInfo={{
                scheduleID: scheduleDetail.scheduleID,
                title: scheduleDetail.title,
                note: scheduleDetail.note,
                workDate: scheduleDetail.workDate,
                startWorkHour: scheduleDetail.startWorkHour,
                startWorkMinute: scheduleDetail.startWorkMinute,
                endWorkHour: scheduleDetail.endWorkHour,
                endWorkMinute: scheduleDetail.endWorkMinute,
                totalHours: 0,
                timeZoneId: scheduleDetail.timeZoneId,
                isHoliday: scheduleDetail.isHoliday,
                isWeekend: false,
                employee: scheduleDetail.employee,
                timeoffDetail: scheduleDetail.timeoffDetail,
              }}
            />
          ) : null}

          <div
            style={{
              backgroundColor: token.colorBgContainer,
              padding: "16px",
              borderRadius: "4px",
              marginTop: "16px",
            }}
          >
            <Space direction="vertical" style={{ width: "100%" }}>
              <Row gutter={[16, 16]}>
                <Col span={2}>
                  <Avatar
                    size="small"
                    icon={
                      <UserOutlined style={{ color: token.colorPrimary }} />
                    }
                  />
                </Col>
                <Col span={22}>
                  <Title level={5} style={{ marginBottom: 4 }}>
                    {scheduleDetail?.employee.firstName}{" "}
                    {scheduleDetail?.employee.lastName}
                  </Title>
                  <Text style={{ fontSize: "12px", display: "block" }}>
                    SomeEmail@gmail.com
                  </Text>
                  <Text style={{ fontSize: "12px" }}>
                    {scheduleDetail?.employee.title}
                  </Text>
                </Col>
              </Row>
            </Space>
          </div>

          <div
            style={{
              backgroundColor: token.colorBgContainer,
              padding: "16px",
              borderRadius: "4px",
              marginTop: "16px",
            }}
          >
            <Space direction="vertical" style={{ width: "100%" }}>
              <Row gutter={[16, 16]}>
                <Col span={2}>
                  <InfoCircleOutlined style={{ color: token.colorPrimary }} />
                </Col>
                <Col span={22}>
                  <Title level={5} style={{ marginBottom: 4 }}>
                    {scheduleDetail?.title}
                  </Title>
                  <Text style={{ fontSize: "12px", display: "block" }}>
                    {`${new Date(
                      scheduleDetail?.workDate ?? new Date(),
                    ).toLocaleDateString("en-us", {
                      timeZone: "UTC",
                      weekday: "long",
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}`}
                  </Text>
                  <Text style={{ fontSize: "12px" }}>
                    {ConvertTo12hr(scheduleDetail?.startWorkHour ?? 0)}:
                    {scheduleDetail?.startWorkMinute === 0
                      ? "00"
                      : scheduleDetail?.startWorkMinute}{" "}
                    {AmPMFormat(scheduleDetail?.startWorkHour ?? 0)}
                    {" - "}
                    {ConvertTo12hr(scheduleDetail?.endWorkHour ?? 0)}:
                    {scheduleDetail?.endWorkMinute === 0
                      ? "00"
                      : scheduleDetail?.endWorkMinute}{" "}
                    {AmPMFormat(scheduleDetail?.endWorkHour ?? 0)}
                  </Text>
                </Col>
              </Row>

              <Divider style={{ margin: "12px 0" }} />

              <Row gutter={[16, 16]}>
                <Col span={2}>
                  <EnvironmentOutlined style={{ color: token.colorPrimary }} />
                </Col>
                <Col span={22}>
                  <Title level={5} style={{ marginBottom: 4 }}>
                    {scheduleDetail?.branch.name}
                  </Title>
                  <Text style={{ fontSize: "12px", display: "block" }}>
                    {scheduleDetail?.branch.address.line1}
                  </Text>
                  <Text style={{ fontSize: "12px" }}>
                    {scheduleDetail?.branch.address.city},{" "}
                    {scheduleDetail?.branch.address.state}{" "}
                    {scheduleDetail?.branch.address.zip}
                  </Text>
                </Col>
              </Row>

              <Divider style={{ margin: "12px 0" }} />

              <Row gutter={[16, 16]}>
                <Col span={2}>
                  <FileOutlined style={{ color: token.colorPrimary }} />
                </Col>
                <Col span={22}>
                  <Text style={{ fontSize: "14px" }}>
                    {scheduleDetail?.note}
                  </Text>
                </Col>
              </Row>

              {scheduleDetail && new Date(scheduleDetail.workDate) >= today ? (
                <Space
                  direction="vertical"
                  style={{ width: "100%", marginTop: 16 }}
                >
                  {scheduleDetail?.timeoffDetail === undefined &&
                  scheduleDetail?.shiftActivity === undefined ? (
                    <>
                      <Button
                        block
                        onClick={() => {
                          setOpenTieOffModal(true);
                        }}
                      >
                        Request Shift Off
                      </Button>
                    </>
                  ) : scheduleDetail?.timeoffDetail !== undefined ? (
                    <>
                      <Button
                        block
                        danger
                        onClick={() => {
                          setOpenTimOffCancelModal(true);
                        }}
                      >
                        Cancel Time Off Request
                      </Button>
                    </>
                  ) : null}

                  {scheduleDetail?.replacementRequest ||
                  scheduleDetail?.shiftActivity !== undefined ? null : (
                    <Button block onClick={showFindReplacementModel}>
                      Find Replacement
                    </Button>
                  )}

                  {isManager ? (
                    <Button danger onClick={showCancleScheduleModal} block>
                      Cancel Shift
                    </Button>
                  ) : null}
                </Space>
              ) : null}

              {isManager &&
              hasNoActivity &&
              isOnOrBeforeToday &&
              !scheduleDetail?.isAbsent ? (
                <Space
                  direction="vertical"
                  style={{ width: "100%", marginTop: 12 }}
                >
                  <Button
                    block
                    onClick={() => {
                      setOpenAbsentModal(true);
                    }}
                  >
                    Mark as Absent
                  </Button>
                </Space>
              ) : null}
            </Space>
          </div>
        </div>
      ),
    },
    {
      key: "activity",
      label: "Activity",
      children: (
        <div style={{ overflowY: "auto", maxHeight: "calc(100vh - 200px)" }}>
          {scheduleDetail && (
            <ScheduleActivity
              scheduleId={scheduleId}
              onUpdate={function (): void {
                SetupdateSchedule(!updateSchedule);
              }}
            />
          )}
        </div>
      ),
    },
    {
      key: "tasks",
      label: "Tasks",
      children: (
        <div style={{ overflowY: "auto", maxHeight: "calc(100vh - 200px)" }}>
          {isManager ? (
            <div style={{ marginBottom: 12 }}>
              <Button
                type="primary"
                onClick={() => {
                  setOpenTaskManagerModal(true);
                }}
              >
                Manage Tasks
              </Button>
            </div>
          ) : null}
          {scheduleDetail?.tasks && scheduleDetail.tasks.length > 0 ? (
            <Collapse
              ghost
              accordion
              style={{ background: "transparent" }}
              expandIconPosition="end"
            >
              {[...scheduleDetail.tasks]
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((task, index) => {
                  const isCompleted =
                    task.status >= 2 || !!task.latestCompletion;
                  const isCompletingTask = completingTaskId === task.id;
                  return (
                    <Collapse.Panel
                      key={task.id}
                      header={
                        <div style={{ width: "100%", paddingRight: 8 }}>
                          <Space
                            direction="vertical"
                            size={6}
                            style={{ width: "100%" }}
                          >
                            <Space
                              align="center"
                              style={{
                                width: "100%",
                                justifyContent: "space-between",
                              }}
                            >
                              <Space align="center" size={10}>
                                <span
                                  onClick={(event) => {
                                    event.stopPropagation();
                                  }}
                                >
                                  {isCompleted ? (
                                    <ConfigProvider
                                      theme={{
                                        token: {
                                          colorPrimary: token.colorSuccess,
                                        },
                                      }}
                                    >
                                      <Radio checked disabled />
                                    </ConfigProvider>
                                  ) : (
                                    <Radio
                                      checked={false}
                                      disabled={!isManager || isCompletingTask}
                                      onChange={() => {
                                        if (isManager && !isCompletingTask) {
                                          void handleCompleteTask(task.id);
                                        }
                                      }}
                                    />
                                  )}
                                </span>
                                <Text strong>
                                  {index + 1}. {task.name}
                                </Text>
                              </Space>
                              <Space align="center" size={6}>
                                {isCompleted ? (
                                  <CheckCircleFilled
                                    style={{
                                      color: token.colorSuccess,
                                      fontSize: 12,
                                    }}
                                  />
                                ) : (
                                  <ClockCircleOutlined
                                    style={{
                                      color: token.colorTextTertiary,
                                      fontSize: 12,
                                    }}
                                  />
                                )}
                                <Tag
                                  color={isCompleted ? "success" : "default"}
                                >
                                  {isCompleted ? "Completed" : "Pending"}
                                </Tag>
                              </Space>
                            </Space>
                            <Space size={6} wrap>
                              <Tag bordered={false}>
                                ~{task.estimatedMinutes} min
                              </Tag>
                              {task.requirePhoto ? (
                                <Tag bordered={false}>Photo required</Tag>
                              ) : null}
                              {task.requireNote ? (
                                <Tag bordered={false}>Note required</Tag>
                              ) : null}
                            </Space>
                          </Space>
                        </div>
                      }
                    >
                      <Card
                        size="small"
                        style={{
                          backgroundColor: token.colorBgContainer,
                          borderColor: token.colorBorder,
                        }}
                      >
                        <Space
                          direction="vertical"
                          size={8}
                          style={{ width: "100%" }}
                        >
                          {task.description ? (
                            <Text>{task.description}</Text>
                          ) : null}
                          {task.latestCompletion ? (
                            <>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                Completed at:{" "}
                                {new Date(
                                  task.latestCompletion.completedAt,
                                ).toLocaleString()}
                              </Text>
                              {task.latestCompletion.completionNote ? (
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  Completion note:{" "}
                                  {task.latestCompletion.completionNote}
                                </Text>
                              ) : null}
                            </>
                          ) : (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Not completed yet
                            </Text>
                          )}
                        </Space>
                      </Card>
                    </Collapse.Panel>
                  );
                })}
            </Collapse>
          ) : (
            <Empty description="No tasks assigned to this schedule" />
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <Drawer
        title={getDrawerTitle()}
        placement="right"
        onClose={onClose}
        open={open}
        width={560}
        extra={
          isManager ? (
            <EditOutlined
              style={{ color: token.colorPrimaryActive, cursor: "pointer" }}
              onClick={handleEditClick}
            />
          ) : null
        }
      >
        {scheduleDetail ? (
          <Tabs
            activeKey={currentTab}
            onChange={setCurrentTab}
            items={tabItems}
          />
        ) : null}
      </Drawer>

      {openTieOffModal && scheduleDetail && (
        <TimeOffModal
          id={scheduleId}
          open={openTieOffModal}
          onCreate={onCreateTimeOff}
          onCancel={() => {
            setOpenTieOffModal(false);
          }}
        />
      )}

      {openTaskManagerModal && scheduleDetail && selectedCompany ? (
        <ScheduleTaskManagerModal
          open={openTaskManagerModal}
          corporationId={selectedCompany.corporationId}
          scheduleDetail={scheduleDetail}
          onCancel={() => {
            setOpenTaskManagerModal(false);
          }}
          onSaved={() => {
            setOpenTaskManagerModal(false);
            SetupdateSchedule(!updateSchedule);
            onRefresh?.();
          }}
        />
      ) : null}

      {openTimOffCancelModal && scheduleDetail && (
        <TimeOffRequestCancel
          timeOffId={scheduleDetail?.timeoffDetail?.timeOffId ?? ""}
          open={openTimOffCancelModal}
          onCreate={onCancelTimeOff}
          onCancel={() => {
            setOpenTimOffCancelModal(false);
          }}
        />
      )}

      {openFindReplacementModel && scheduleDetail && (
        <FindScheduleReplacement
          open={openFindReplacementModel}
          schedule={scheduleDetail}
          onCreate={onCreateReplacement}
          onCancel={() => setOpenFindReplacementModel(false)}
        />
      )}

      <Modal
        title="Are you sure you want to cancel this shift?"
        open={cancelScheduleOpen}
        onOk={handleCancelScheduleOk}
        confirmLoading={confirmLoading}
        onCancel={() => setCancelScheduleOpen(false)}
      />

      <AbsentScheduleModal
        open={openAbsentModal}
        onClose={() => setOpenAbsentModal(false)}
        onMarkAbsent={handleMarkAbsent}
        scheduleName={`${scheduleDetail?.employee.firstName ?? ""} ${
          scheduleDetail?.employee.lastName ?? ""
        } - ${scheduleDetail?.title ?? ""}`}
      />

      {isFormOpen && scheduleDetail && (
        <EditScheduleForm
          open={true}
          onCreate={onCreate}
          onCancel={handleCancel}
          scheduleDetails={scheduleDetail}
          cropId={selectedCompany?.corporationId ?? 0}
        />
      )}
    </>
  );
};

export default ScheduleDetailsDrawer;
